import type { FC } from "react";
import { useEffect, useRef } from "react";

import { makeStyles } from "@mui/styles";

import { Box, Link, Theme, Typography, useTheme } from "@mui/material";
import StatusChecker from "../../components/StatusChecker";
import CustomButton from "../../components/CustomButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";

import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../hooks/useSettings";

import { getScriptString } from "../../generic-utils";
interface AddPixelProps {
  goToNext: () => void;
  goToPrevious: () => void;
  createdWebId: string;
  modalView?: boolean;
  onPrimaryButtonClick?: () => void;
  onPixelVerified?: () => void;
}

const AddPixel: FC<AddPixelProps> = ({
  goToNext,
  goToPrevious,
  createdWebId,
  modalView,
  onPrimaryButtonClick,
  onPixelVerified,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    checkPixelStatus,
    isConnected: isActive,
    isCheckLoading,
  } = useSettings(false);

  const prevIsActive = useRef(isActive);

  useEffect(() => {
    // If pixel became connected and we have a callback
    if (!prevIsActive.current && isActive && onPixelVerified) {
      onPixelVerified();
    }
    prevIsActive.current = isActive;
  }, [isActive, onPixelVerified]);

  const SCRIPT_STRING = getScriptString(createdWebId);

  return (
    <Box className={classes.container}>
      <Box
        className={classes.formContainer}
        style={{ marginTop: modalView ? 0 : 65 }}
      >
        <Typography textAlign='center' className={classes.title}>
          {t("howToInstallPixel.title")}
        </Typography>
        <Box style={{ display: "flex", flexDirection: "row" }}>
          <Typography className={classes.listItemNumber}>1.</Typography>
          <Typography className={classes.listItemText}>
            {t("howToInstallPixel.first")}
          </Typography>
        </Box>

        <Box className={classes.scriptContainer}>
          <Typography>{SCRIPT_STRING}</Typography>
          <Box style={{ color: theme.palette.main.secondary.u1, width: 40 }}>
            <ContentCopyIcon
              style={{ fontSize: 26, cursor: "pointer" }}
              onClick={() => {
                navigator.clipboard.writeText(SCRIPT_STRING);
                showFlashMessageWithTimeout(
                  dispatch,
                  "Script byl zkopírovan.",
                  "success",
                );
              }}
            />
          </Box>
        </Box>

        <Box
          style={{ display: "flex", flexDirection: "row", marginBottom: 24 }}
        >
          <Typography className={classes.listItemNumber}>2.</Typography>
          <Typography className={classes.listItemText}>
            {t("howToInstallPixel.second")}
          </Typography>
        </Box>

        <Box style={{ display: "flex", flexDirection: "row" }}>
          <Typography className={classes.listItemNumber}>3.</Typography>
          <Typography className={classes.listItemText}>
            {t("howToInstallPixel.third")}
          </Typography>
        </Box>
        <StatusChecker
          isActive={isActive}
          isLoading={isCheckLoading}
          onClick={checkPixelStatus}
        />

        <Box className={classes.buttonContainer}>
          <Link
            href='https://www.overenyweb.cz/napoveda/jak-propojit-overenyweb-s-vasim-systemem/'
            target='_blank'
          >
            <Typography>{t("howToInstallPixel.help")}</Typography>
          </Link>
          {!modalView && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <CustomButton
                title={t("back")}
                type='secondary'
                onClick={goToPrevious}
                style={{ width: 100 }}
              />
              <CustomButton
                title={t("continue")}
                onClick={() => {
                  if (!isActive && onPrimaryButtonClick) {
                    onPrimaryButtonClick();
                    return;
                  }

                  goToNext();
                }}
                style={{ width: 120 }}
              />
            </Box>
          )}
          {modalView && onPrimaryButtonClick && (
            <CustomButton
              title={t("close")}
              type='primary'
              size='medium'
              onClick={onPrimaryButtonClick}
              style={{ width: 87 }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  formContainer: {
    width: 600,
    display: "flex",
    flexDirection: "column",
  },
  buttonContainer: {
    height: 52,
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 30,
  },
  title: {
    color: theme.palette.main.secondary.u1,
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 20,
  },
  scriptContainer: {
    flex: 1,
    backgroundColor: theme.palette.natural.t6,
    marginTop: 24,
    marginBottom: 24,
    padding: 12,
    border: `1px solid ${theme.palette.natural.t8}`,
    borderRadius: 4,
    display: "flex",
  },
  listItemNumber: {
    color: theme.palette.main.secondary.o,
    marginRight: 8,
    fontSize: 16,
    fontWeight: "bold",
  },
  listItemText: {
    fontSize: 16,
    color: theme.palette.natural.t,
  },
}));

export default AddPixel;
